import { auditReports } from '@mi-front/api-clients';
import { Spinner } from '@mi-front/ui';
import { GipExportConfigInfo } from '../../../export/info/GipExportConfigInfo';
import { GipExportLauncher } from '../export-modal/GipExportLauncher';

import { JobProgress } from '@app/components/base/JobProgress';
import { useJobProgressMonitor } from '@app/core/hooks/useJobProgressMonitor';
import { KeyValue } from '@app/core/components/KeyValue';
import { DownloadExportBtn } from '@app/modules/audit/modules/aod/screens/aod-rule-view/custom/gip/DownloadExportBtn';
import { useCreatedReport } from '@app/modules/audit/modules/global-audit/hooks/useCreatedReport';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useReportExportApi } from './adapters/useReportExportApi';
import { ReExportSection } from './ReExportSection';

interface ReportExportInfoProps {
  report: auditReports.GipReportDto | auditReports.GipAltReportDto;
}

/**
 * Export info component that handles both GIP and GIP Alternative reports.
 *
 * This component uses the modern auditReports API with job-based async export
 * for both report types, providing a consistent user experience.
 *
 * @param report - The report data (GipReport or GipAlternativeReport)
 */
export const ReportExportInfo = ({ report }: ReportExportInfoProps) => {
  const export_ = report.data.export;
  const exportCompleted = !!export_?.storageUri;
  const exportInProgress = !!export_?.jobId && !exportCompleted;
  const api = useReportExportApi(report.type);

  const { data: jobStatus, isLoading: isStatusLoading } = api.useExportStatus(
    report.id || '',
    exportInProgress,
  );

  useJobProgressMonitor({
    jobStatus,
    queryKeysToInvalidate: useCreatedReport.queryKey(report.id),
  });

  const { data: exportUrlData, isLoading: isUrlLoading } = api.useExportUrl(
    report.id,
    exportCompleted,
  );

  // No export case - when export is null or no jobId
  if (!export_ || (!exportInProgress && !exportCompleted)) {
    return (
      <div>
        <GipExportLauncher
          reportType={report.type}
          onExport={async (config) => {
            await api.exportReport(report.id, config as auditReports.GipEvidenceExportOptionsDto);
          }}
        />
      </div>
    );
  }

  if (jobStatus?.status === auditReports.PublicJobDtoStatusEnum.FAILED) {
    return (
      <div>
        {jobStatus && <JobProgress job={jobStatus} />}
        <KeyValue
          label={
            <FormattedMessage id="audit.reportsView.exportFailed" defaultMessage="Export fallido" />
          }
        >
          <GipExportLauncher
            retry
            reportType={report.type}
            onExport={async (config) => {
              await api.exportReport(report.id, config as auditReports.GipEvidenceExportOptionsDto);
            }}
          />
        </KeyValue>
      </div>
    );
  }

  // Main render case - export in progress or completed
  return (
    <div>
      {jobStatus && jobStatus.status !== auditReports.PublicJobDtoStatusEnum.COMPLETED && (
        <JobProgress job={jobStatus} />
      )}
      {(isStatusLoading || isUrlLoading) && <Spinner className="ml-3" />}
      {exportCompleted && exportUrlData && (
        <DownloadExportBtn fetchUrl={() => Promise.resolve(exportUrlData.downloadUrl)} />
      )}
      {export_ && (
        <Box sx={{ mt: 3, maxWidth: '800px' }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>
                <FormattedMessage
                  id="audit.reportsView.exportInfo"
                  defaultMessage="Configuración de export"
                />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <GipExportConfigInfo inDialog config={export_.options} />
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
      {exportCompleted && (
        <ReExportSection
          reportId={report.id}
          reportType={report.type}
          exportOptions={export_.options}
          onExport={async (config) => {
            await api.exportReport(report.id, config as auditReports.GipEvidenceExportOptionsDto);
          }}
        />
      )}
    </div>
  );
};
